import React, { useState, useCallback } from 'react';
import { PenTool, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { DocumentSignerDialog } from './SignaturePad';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "Sign" button that opens the full DocumentSignerDialog
 * with fetched content + signature fields. Usable from anywhere a user
 * encounters a document (Shared With Me, Tasks, etc).
 *
 * Props:
 *   doc              — { document_id, name, file_type }
 *   onSigned         — optional callback after successful signature
 *   variant,size,className — forwarded to Button
 *   children         — optional custom button contents (default "Sign")
 */
const SignDocumentButton = ({
  doc,
  onSigned,
  variant = 'outline',
  size = 'sm',
  className = '',
  children,
  dataTestId,
}) => {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signDocContent, setSignDocContent] = useState(null);
  const [signatureFields, setSignatureFields] = useState([]);
  const [existingSignatures, setExistingSignatures] = useState([]);

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const handleOpen = async () => {
    if (!doc?.document_id) {
      toast.error('No document linked to this item');
      return;
    }
    setLoading(true);
    setSignatureFields([]);
    setSignDocContent(null);
    setExistingSignatures([]);
    try {
      const [contentRes, fieldsRes, sigsRes] = await Promise.all([
        fetch(`${API_URL}/api/documents/${doc.document_id}/content`, {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(`${API_URL}/api/documents/${doc.document_id}/signature-fields`, {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(`${API_URL}/api/documents/${doc.document_id}/signatures`, {
          credentials: 'include',
          headers: authHeaders(),
        }),
      ]);

      if (!contentRes.ok) {
        const err = await contentRes.json().catch(() => ({}));
        toast.error(err.detail || 'Cannot access this document');
        setLoading(false);
        return;
      }

      const content = await contentRes.json();
      setSignDocContent(content);

      if (fieldsRes.ok) {
        const data = await fieldsRes.json();
        setSignatureFields(data.fields || []);
      }

      if (sigsRes.ok) {
        const data = await sigsRes.json();
        setExistingSignatures(data.signatures || data || []);
      }

      setOpen(true);
    } catch (e) {
      toast.error('Failed to load document for signing');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (signatureData, position, metadata) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}/sign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          signature_data: signatureData,
          position: position || null,
          signer_typed_name: metadata?.signer_typed_name || null,
          signed_at_display: metadata?.signed_at_display || null,
          name_position: metadata?.name_position || null,
          sig_scale: metadata?.sig_scale || 1,
          name_scale: metadata?.name_scale || 1,
          field_values: metadata?.field_values || null,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Sign failed');
        return;
      }

      toast.success('Document signed!');

      // Refresh content + signatures so the dialog reflects the newly baked signature
      try {
        const [contentRes, sigsRes] = await Promise.all([
          fetch(`${API_URL}/api/documents/${doc.document_id}/content`, {
            credentials: 'include',
            headers: authHeaders(),
          }),
          fetch(`${API_URL}/api/documents/${doc.document_id}/signatures`, {
            credentials: 'include',
            headers: authHeaders(),
          }),
        ]);
        if (contentRes.ok) setSignDocContent(await contentRes.json());
        if (sigsRes.ok) {
          const data = await sigsRes.json();
          setExistingSignatures(data.signatures || data || []);
        }
      } catch {
        /* ignore refresh error */
      }

      if (onSigned) onSigned(doc);
    } catch (e) {
      toast.error('Failed to sign document');
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={loading}
        variant={variant}
        size={size}
        className={className}
        data-testid={dataTestId || `sign-doc-btn-${doc?.document_id}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <PenTool className="w-4 h-4 mr-2" />
        )}
        {children || 'Sign'}
      </Button>

      <DocumentSignerDialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            setOpen(false);
            setSignDocContent(null);
            setSignatureFields([]);
            setExistingSignatures([]);
          }
        }}
        documentContent={signDocContent?.content || null}
        documentType={signDocContent?.file_type || doc?.file_type || ''}
        documentName={doc?.name || ''}
        existingSignatures={existingSignatures}
        onSign={handleSign}
        signatureFields={signatureFields}
        currentUserId={user?.user_id}
      />
    </>
  );
};

export default SignDocumentButton;
